import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {RankedModeTypes, RankedSeasonTypes} from '../../../../shared/GameEnums'
import {TranslationObject} from '@colonist/i18n'
import {RankedDivisionTypes, RankedSubDivisionTypes} from '../../../../shared/leaderboard_data/LeaderboardEnums'
import {RankedLockedState, RankedMMRState, RankedPlacementState, RankedUserStateController, RankedUserStateType} from '@colonist/leaderboard-types'
import {ClientUserState} from '../ClientUserState'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {Sound} from '../../../shared/scripts/Sound'
import {UIUrlParameterNames} from '../../../shared/scripts/ui_html/UIUrlController'
import {createDivIn, createImgIn, createLinkIn} from '../../../shared/scripts/ui_utils/DomUtils'
import {UILeaderboardUtils} from '../ui_leaderboard/UILeaderboardUtils'
import {UIProfileRankedProgressBarController} from './UIProfileRankedProgressBarController'
import {clientI18n} from '../ClientI18n'
import {UIImagePathController} from '../UIEnums.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {uiManagerEvents} from '../EntryShared'
import {navigateToPage} from '../ui_utils/ClientUtils.ts'

export class UIProfileRankedController {
    private _username: string
    private rankedData: AjaxDataTypes.ProfileDataRanked[]

    currentSeason: RankedSeasonTypes
    private isSelectionCurrentSeason: boolean
    private readonly progressContainerIdBase = 'ranked_mode_player_progress'

    private readonly baseGameIdentifier = 'base'
    private readonly oneVersusOneIdentifier = '1v1'
    private readonly citiesAndKnightsIdentifier = 'citiesandknights'
    private readonly statContainerClass = 'ranked_mode_player_stat'
    private readonly playNowLinkText: TranslationObject = {key: 'strings:colonistCommon.rankedState.playNow'}
    private readonly noGamesPlayedText: TranslationObject = {key: 'strings:colonistCommon.rankedState.noGamesPlayed'}

    private readonly renderMobileVersion: boolean

    constructor(username: string, renderMobileVersion: boolean = false) {
        this._username = username
        this.renderMobileVersion = renderMobileVersion

        this.setPlayNowButtonClickHandlers()
    }

    set username(username: string) {
        this._username = username
    }

    // eslint-disable-next-line class-methods-use-this
    private setPlayNowButtonClickHandlers(): void {
        const shouldHide = ClientUserState.userState?.username != decodeURIComponent(this._username)

        const playNowButtons = document.getElementsByClassName('ranked-play-now') as HTMLCollectionOf<HTMLDivElement>
        for(const playNowButton of playNowButtons) {
            if(shouldHide) {
                playNowButton.style.display = 'none'
                continue
            }

            playNowButton.onclick = (e: MouseEvent) => {
                e.stopPropagation()
                uiManagerEvents.handleUrlRedirectViaParameters(UIUrlParameterNames.EnterMatchmaking, playNowButton.dataset.matchmakingType)
            }
        }
    }

    async fetchRankedDataForSelectedSeason(selectedSeason: RankedSeasonTypes) {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.ranked(this._username), selectedSeason.toString())
        if(response == undefined) return

        this.rankedData = response.json
        for(const rankedData of this.rankedData) {
            if(rankedData.currentRankedState == undefined) continue
            rankedData.currentRankedState = RankedUserStateController.deserialize(rankedData.currentRankedState)
        }
        this.isSelectionCurrentSeason = selectedSeason == this.currentSeason
        this.setRankedDataUI()
    }

    private setRankedDataUI() {
        this.hackyLogicToHideAllCards()
        for(const profileDataRanked of this.rankedData) {
            const rankedModeString = this.getRankedModeString(profileDataRanked.modeType)
            if(!rankedModeString) return
            const cardId = `ranked_mode_player_info_card_${rankedModeString}`
            const rankedModeCardDiv = document.getElementById(cardId) as HTMLDivElement
            rankedModeCardDiv.hidden = false
            this.createClickEventToRouteToLeaderboard(rankedModeCardDiv, profileDataRanked.modeType)

            const statsContainerId = `ranked_mode_player_stats_${rankedModeString}_container`
            const statsContainerDiv = document.getElementById(statsContainerId) as HTMLDivElement
            statsContainerDiv.replaceChildren()

            const progressContainerDivId = `${this.progressContainerIdBase}_${rankedModeString}_container`
            const progressContainerDiv = document.getElementById(progressContainerDivId) as HTMLDivElement
            progressContainerDiv.replaceChildren()

            profileDataRanked.currentRankedState != undefined ? this.displayCurrentSeasonState(profileDataRanked, progressContainerDiv) : this.populatePastSeasonProgress(profileDataRanked, progressContainerDiv)
            this.populateBasicStats(profileDataRanked, statsContainerDiv)
            this.populateRankingStats(profileDataRanked, statsContainerDiv)
        }
    }

    private hackyLogicToHideAllCards() {
        const allNamesToClear = [this.baseGameIdentifier, this.oneVersusOneIdentifier, this.citiesAndKnightsIdentifier]
        for(const name of allNamesToClear) {
            const cardId = `ranked_mode_player_info_card_${name}`
            const rankedModeCardDiv = document.getElementById(cardId) as HTMLDivElement
            rankedModeCardDiv.hidden = true
        }
    }

    private displayCurrentSeasonState(profileDataRanked: AjaxDataTypes.ProfileDataRanked, progressContainerDiv: HTMLDivElement) {
        if(profileDataRanked.currentRankedState == undefined) return
        switch(profileDataRanked.currentRankedState.type) {
            case RankedUserStateType.RankedLocked: this.displayRankedLocked(profileDataRanked.currentRankedState as RankedLockedState, progressContainerDiv); break
            case RankedUserStateType.Placement:
                this.displayRankedPlacementState(profileDataRanked.currentRankedState as RankedPlacementState, progressContainerDiv)
                if(ClientUserState.userState?.username == decodeURIComponent(this._username)) this.createPlayNowLink(profileDataRanked, progressContainerDiv)
                break
            case RankedUserStateType.Ranked:
                if(profileDataRanked.division != RankedDivisionTypes.None) this.createDivisionIcon(profileDataRanked.division, profileDataRanked.subdivision, progressContainerDiv)
                UIProfileRankedController.displayRankedProgressBar(profileDataRanked.currentRankedState as RankedMMRState, progressContainerDiv)
                break
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private displayRankedLocked(rankedLockedState: RankedLockedState, progressContainerDiv: HTMLDivElement): void {
        const rankedLockedDiv = createDivIn(progressContainerDiv, '')
        const statusText = rankedLockedState.getFindGamePopupStatusText()
        rankedLockedDiv.innerText = clientI18n.t(statusText.key, statusText.options)
    }

    // eslint-disable-next-line class-methods-use-this
    private displayRankedPlacementState(rankedPlacementState: RankedPlacementState, progressContainerDiv: HTMLDivElement): void {
        const placementGamesDiv = createDivIn(progressContainerDiv, '')
        const statusText = rankedPlacementState.getFindGamePopupStatusText()
        placementGamesDiv.innerText = clientI18n.t(statusText.key, statusText.options)
    }

    private static displayRankedProgressBar(rankedMMRState: RankedMMRState, progressContainerDiv: HTMLDivElement) {
        new UIProfileRankedProgressBarController(rankedMMRState.currentDivisionState, rankedMMRState.currentMMR, progressContainerDiv)
    }

    private populatePastSeasonProgress(profileDataRanked: AjaxDataTypes.ProfileDataRanked, progressContainerDiv: HTMLDivElement) {
        profileDataRanked.division == RankedDivisionTypes.None ? this.createNoGamesPlayedText(progressContainerDiv) : this.createDivisionIcon(profileDataRanked.division, profileDataRanked.subdivision, progressContainerDiv)
    }

    // eslint-disable-next-line class-methods-use-this
    private createDivisionIcon(currentDivision: RankedDivisionTypes, currentSubdivsion: RankedSubDivisionTypes, progressContainerDiv: HTMLDivElement): void {
        const divisionIconContainer = createDivIn(progressContainerDiv, '')
        const divisionImage = UIImagePathController.getRankedDivisionImage(currentDivision)
        createImgIn(divisionIconContainer, divisionImage)

        const subdivisionImagePath = UIImagePathController.getRankedSubdivisionImage(currentSubdivsion)
        if(subdivisionImagePath != undefined) createImgIn(divisionIconContainer, subdivisionImagePath)

        const divisionName = createDivIn(progressContainerDiv, '')
        const subdivisionName = currentSubdivsion == RankedSubDivisionTypes.None ? '' : RankedSubDivisionTypes[currentSubdivsion]
        divisionName.innerText = `${RankedDivisionTypes[currentDivision]} ${subdivisionName}`
    }

    private getRankedModeString(rankedModeType: RankedModeTypes) {
        switch(rankedModeType) {
            case RankedModeTypes.Classic4P:
                return this.baseGameIdentifier
            case RankedModeTypes.Classic1v1:
                return this.oneVersusOneIdentifier
            case RankedModeTypes.CitiesAndKnights4P:
                return this.citiesAndKnightsIdentifier
            default:
                return ''
        }
    }

    private populateBasicStats(profileDataRanked: AjaxDataTypes.ProfileDataRanked, statsContainerDiv: HTMLDivElement) {
        const winRateText = profileDataRanked.winRate == '-' ? profileDataRanked.winRate : `${profileDataRanked.winRate}%`
        this.createStatDiv(statsContainerDiv, {key: 'strings:leaderboardPage.tableHeaders.rating'}, profileDataRanked.skillRating)
        this.createStatDiv(statsContainerDiv, {key: 'strings:leaderboardPage.tableHeaders.games'}, profileDataRanked.gamesPlayed)
        this.createStatDiv(statsContainerDiv, {key: 'strings:leaderboardPage.tableHeaders.winPercentage'}, winRateText)
    }

    private populateRankingStats(profileDataRanked: AjaxDataTypes.ProfileDataRanked, statsContainerDiv: HTMLDivElement) {
        profileDataRanked.rankedStanding.forEach((rankedStanding) => {
            this.createRankingStatsForRegionalStanding(rankedStanding, statsContainerDiv)
        })
    }

    private createRankingStatsForRegionalStanding(rankedStanding: AjaxDataTypes.ProfileDataRankedStanding, statsContainerDiv: HTMLDivElement) {
        const doesRankingExist = rankedStanding.rank != '-'
        const numberSignToAdd = doesRankingExist ? '#' : ''
        const rankingWithNumberSign = numberSignToAdd + rankedStanding.rank
        const rankStandingName = UILeaderboardUtils.tLeaderboardTabName(rankedStanding.name)
        this.createStatDiv(statsContainerDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rankStandingName}}, rankingWithNumberSign)
    }

    private createStatDiv(progressContainerDiv: HTMLDivElement, statName: TranslationObject, statValue: string): void {
        const statContainerDiv = createDivIn(progressContainerDiv, this.statContainerClass)
        const statNameDiv = createDivIn(statContainerDiv, '') as HTMLDivElement
        const statValueDiv = createDivIn(statContainerDiv, 'ranked_mode_player_stat_value') as HTMLDivElement
        statNameDiv.innerText = clientI18n.t(statName.key, statName.options)
        statValueDiv.innerText = statValue
    }

    private createPlayNowLink(data: AjaxDataTypes.ProfileDataRanked, progressContainerDiv: HTMLDivElement) {
        if(this.renderMobileVersion) return
        const linkHref = ''
        const playNowLink = createLinkIn(progressContainerDiv, linkHref, this.playNowLinkText, '')
        playNowLink.onclick = (event: MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            Sound.playSound(Sound.SoundType.Click)
            uiManagerEvents.handleUrlRedirectViaParameters(UIUrlParameterNames.EnterMatchmaking, data.matchmakingRedirectType)
        }
    }

    private createNoGamesPlayedText(progressContainerDiv: HTMLDivElement): void {
        const noGamesPlayedDiv = createDivIn(progressContainerDiv, '')
        noGamesPlayedDiv.innerText = clientI18n.t(this.noGamesPlayedText.key, this.noGamesPlayedText.options)
    }

    private createClickEventToRouteToLeaderboard(cardDiv: HTMLDivElement, mode: RankedModeTypes) {
        if(this.renderMobileVersion) return
        if(UIUtilsEjsVariables.isUserOnMobileApp || UIUtilsEjsVariables.isUserOnDiscord) return
        if(!this.isSelectionCurrentSeason) {
            cardDiv.onclick = () => undefined
            cardDiv.style.cursor = 'default'
            return
        }
        cardDiv.style.cursor = 'pointer'
        cardDiv.onclick = () => {
            navigateToPage(`/leaderboards/${RankedModeTypes[mode]}`)
        }
    }
}
